import {useEffect, useSyncExternalStore} from "react";
import {
	getDsContext,
	isDsContextReady,
	onDsContextChange,
} from "@/utils/dsContext";
import {ScoutingProfilesAPI} from "@/api/scouting_profiles";
const empty = () => ({status: "idle", data: null, error: null, busy: false});
const listeners = new Set();
const subscribe = (listener) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};
const emit = () => listeners.forEach((fn) => fn());
const providers = new Map();
onDsContextChange(() => {
	providers.clear();
	emit();
});
const provider = (id) => {
	if (!providers.has(id))
		providers.set(id, {state: empty(), pending: null, revision: 0});
	return providers.get(id);
};
const patch = (entry, changes) => {
	entry.state = {...entry.state, ...changes};
	emit();
};
export const profileData = (id) => {
	getDsContext();
	return provider(id).state;
};
const read = (entry, load, force = false) => {
	const context = getDsContext();
	if (!isDsContextReady())
		return Promise.reject(Error("Sessione non disponibile."));
	if (entry.pending && !force) return entry.pending;
	const ticket = ++entry.revision;
	patch(entry, {
		status:
			entry.state.status === "ready" && entry.state.data ? "ready" : "loading",
		refreshing: true,
		error: null,
	});
	const request = load()
		.then((data) => {
			if (context !== getDsContext() || entry.revision !== ticket)
				throw Error("Risposta superata.");
			patch(entry, {status: "ready", data, error: null, refreshing: false});
			return data;
		})
		.catch((error) => {
			if (context === getDsContext() && entry.revision === ticket)
				patch(entry, {status: "error", data: null, error, refreshing: false});
			throw error;
		})
		.finally(() => {
			if (entry.pending === request) entry.pending = null;
		});
	entry.pending = request;
	return request;
};
export const refreshProfile = (id, force = false) => {
	getDsContext();
	return read(provider(id), () => ScoutingProfilesAPI.get(id), force);
};
export const saveProfile = async (id, data) => {
	getDsContext();
	const entry = provider(id),
		context = getDsContext();
	if (
		!isDsContextReady() ||
		entry.state.status !== "ready" ||
		!entry.state.data?.canWrite ||
		entry.state.busy
	)
		throw Error("Modifica non disponibile.");
	++entry.revision;
	entry.pending = null;
	patch(entry, {busy: true});
	try {
		const result = await ScoutingProfilesAPI.save(id, data);
		if (context !== getDsContext()) throw Error("Sessione cambiata.");
		patch(entry, {
			status: "ready",
			data: result,
			error: null,
			refreshing: false,
		});
		return result;
	} finally {
		if (context === getDsContext()) patch(entry, {busy: false});
	}
};
function useData(snapshot, refresh) {
	const context = getDsContext();
	const current = useSyncExternalStore(subscribe, snapshot, snapshot);
	useEffect(() => {
		if (isDsContextReady()) refresh().catch(() => {});
		const reload = () => {
			if (!document.hidden && !snapshot().busy && isDsContextReady())
				refresh().catch(() => {});
		};
		window.addEventListener("focus", reload);
		const timer = setInterval(reload, 30000);
		return () => {
			window.removeEventListener("focus", reload);
			clearInterval(timer);
		};
	}, [context, refresh, snapshot]);
	return {
		...current,
		canWrite:
			current.status === "ready" && !!current.data?.canWrite && !current.busy,
		refresh,
	};
}
const readers = new Map();
export const useScoutingProfile = (id) => {
	if (!readers.has(id))
		readers.set(id, {
			snapshot: () => profileData(id),
			refresh: (force = false) => refreshProfile(id, force),
		});
	const pair = readers.get(id);
	return useData(pair.snapshot, pair.refresh);
};
